/**
 * Branded confirm dialogs, callable from ANY handler (Kiara 8/25):
 *
 *   if (!(await confirmDialog({ title: 'Discard this room?', message: '…' }))) return;
 *
 * Replaces every window.confirm() in the panel — the native browser popup
 * reads as "the site is about to throw you out", carries the vercel.app
 * hostname, and can't be styled. This resolves a promise through the ONE
 * <ConfirmDialogHost /> mounted at the app root, which renders the house
 * ConfirmationModal.
 *
 * Fallback: if the host isn't mounted (a test, a detached render), it
 * degrades to window.confirm so a guard can never silently answer "yes".
 */
import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

type Pending = { opts: ConfirmOptions; resolve: (ok: boolean) => void };

let listener: ((p: Pending) => void) | null = null;

export const confirmDialog = (opts: ConfirmOptions): Promise<boolean> =>
  new Promise(resolve => {
    if (listener) listener({ opts, resolve });
    else resolve(window.confirm(`${opts.title}\n\n${opts.message}`));
  });

export const ConfirmDialogHost: FC = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  useEffect(() => {
    listener = setPending;
    return () => { listener = null; };
  }, []);
  if (!pending) return null;
  const finish = (ok: boolean) => {
    pending.resolve(ok);
    setPending(null);
  };
  return (
    <ConfirmationModal
      isOpen
      title={pending.opts.title}
      message={pending.opts.message}
      confirmText={pending.opts.confirmText ?? 'OK'}
      cancelText={pending.opts.cancelText ?? 'Cancel'}
      onConfirm={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );
};
